import React, { useState, useEffect } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { CircularProgress } from "@mui/material";
import {
  Button,
  IconButton,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mhome/ui";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import AddIcon from "@mui/icons-material/Add";
import {
  listCanvas,
  upsertCanvas,
  deleteCanvas,
  reorderCanvas,
} from "../service/ui-service";
import { useWebSocket } from "../ws/websocket";
import { useRecoilValue } from "recoil";
import { wsReadyState } from "../global-state";
import { useToast } from "../common/toast";
import LoadingSpinner from "../common/loading";
import { useComponentBackgroundColor } from "../common/adaptive-theme-provider";
import { spacingToPx } from "../lib/utils";
import { cn } from "../lib/utils";

export default function CanvasManage() {
  const getComponentBgColor = useComponentBackgroundColor();
  const [canvasList, setCanvasList] = useState([]);
  const [hoveredCanvasId, setHoveredCanvasId] = useState(null);
  const wsReady = useRecoilValue(wsReadyState);
  const { sendAndWaitWs } = useWebSocket();
  const { successToast, errorToast } = useToast();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedCanvas, setSelectedCanvas] = useState(null);
  const [editName, setEditName] = useState("");
  const [newCanvasName, setNewCanvasName] = useState("");
  const [isRunning, setIsRunning] = useState("");
  const [loading, setLoading] = useState("");

  useEffect(() => {
    console.log("wsReady", wsReady);
    if (wsReady) {
      handleListCanvas();
    }
  }, [wsReady]);

  const handleListCanvas = async () => {
    setLoading(true);
    try {
      const resp = await listCanvas(sendAndWaitWs);
      console.log(resp);
      if (resp.canvas) {
        setCanvasList(resp.canvas);
        console.log(resp.canvas);
      } else {
        errorToast("Failed to get canvas list");
      }
    } catch (error) {
      errorToast("Error loading canvas list");
      console.error("List canvas error:", error);
    }
    setLoading(false);
  };

  const handleCreateCanvas = async () => {
    if (!newCanvasName.trim()) {
      errorToast("Canvas name cannot be empty");
      return;
    }
    setIsRunning("create");

    try {
      const response = await upsertCanvas(sendAndWaitWs, {
        name: newCanvasName.trim(),
      });

      if (response) {
        successToast("Canvas created successfully");
        handleListCanvas();
      } else {
        errorToast("Failed to create canvas");
      }
    } catch (error) {
      errorToast("Error creating canvas");
      console.error("Create canvas error:", error);
    }
    setCreateDialogOpen(false);
    setNewCanvasName("");
    setIsRunning("");
  };

  const handleSave = async () => {
    if (!editName.trim()) {
      errorToast("Canvas name cannot be empty");
      return;
    }
    setIsRunning("save");

    try {
      const response = await upsertCanvas(sendAndWaitWs, {
        id: selectedCanvas.id,
        name: editName.trim(),
      });

      if (response) {
        successToast("Canvas updated");
        handleListCanvas();
      } else {
        errorToast("Failed to update canvas");
      }
    } catch (error) {
      errorToast("Error updating canvas");
      console.error("Update canvas error:", error);
    }
    setIsRunning("");
    setEditDialogOpen(false);
  };

  const confirmDelete = async () => {
    setIsRunning("delete");
    try {
      const response = await deleteCanvas(
        sendAndWaitWs,
        selectedCanvas.id,
        selectedCanvas?.hasCards || false
      );

      if (response) {
        successToast("Canvas deleted successfully");
        handleListCanvas();
      } else {
        errorToast("Failed to delete canvas");
      }
    } catch (error) {
      errorToast("Error deleting canvas");
      console.error("Delete canvas error:", error);
    }
    setIsRunning("");
    setDeleteDialogOpen(false);
  };

  const onDragEnd = async (result) => {
    // If dropped outside, don't change anything
    if (!result.destination) {
      return;
    }

    // If dropped in the same position, no change needed
    if (result.destination.index === result.source.index) {
      return;
    }

    // Reorder the items
    const items = Array.from(canvasList);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update state immediately to fix positions
    setCanvasList(items);

    try {
      const newIndexes = {};
      items.forEach((canvas, index) => {
        newIndexes[canvas.id] = index;
      });

      const response = await reorderCanvas(sendAndWaitWs, newIndexes);

      if (!response) {
        errorToast("Failed to save canvas order");
        handleListCanvas();
      }
    } catch (error) {
      errorToast("Error saving canvas order");
      console.error("Reorder canvas error:", error);
      handleListCanvas();
    }
  };

  const handleEdit = (canvas) => {
    setSelectedCanvas(canvas);
    setEditName(canvas.name);
    setEditDialogOpen(true);
  };

  const handleDelete = (canvas) => {
    setSelectedCanvas(canvas);
    setDeleteDialogOpen(true);
  };

  const handleForceDelete = (canvas) => {
    setSelectedCanvas(canvas);
    setDeleteDialogOpen(true);
  };

  return (
    <>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="p-6">
          <div className="mb-6">
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
              className="normal-case shadow-none"
            >
              New Canvas
            </Button>
          </div>

          <DragDropContext
            onDragEnd={(result) => {
              // Restore scrolling when drag ends
              document.body.style.overflow = "";
              // Clear hover state when drag ends
              setHoveredCanvasId(null);
              onDragEnd(result);
            }}
            enableDefaultSensors={true}
            onDragStart={() => {
              // Only prevent scrolling when actually dragging
              document.body.style.overflow = "hidden";
              // Clear hover state when drag starts
              setHoveredCanvasId(null);
            }}
          >
            <Droppable droppableId="canvas-list">
              {(provided) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className="flex flex-col gap-4"
                >
                  {canvasList.length > 0 ? (
                    canvasList.map((canvas, index) => (
                      <Draggable
                        key={canvas.id}
                        draggableId={String(canvas.id)}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <Paper
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            elevation={snapshot.isDragging ? 5 : 0}
                            variant="outlined"
                            className={cn(
                              "p-4 flex items-center gap-4 select-none",
                              snapshot.isDragging
                                ? "cursor-grabbing"
                                : "cursor-grab",
                              snapshot.isDragging
                                ? ""
                                : "transition-colors duration-200"
                            )}
                            style={{
                              ...provided.draggableProps.style,
                              backgroundColor: snapshot.isDragging
                                ? "hsl(var(--accent))"
                                : hoveredCanvasId === canvas.id &&
                                  !snapshot.isDragging
                                ? "hsl(var(--accent))"
                                : getComponentBgColor("hsl(var(--card))"),
                              WebkitTapHighlightColor: "transparent",
                              touchAction: snapshot.isDragging
                                ? "none"
                                : "manipulation",
                            }}
                          >
                            <div
                              {...provided.dragHandleProps}
                              className={cn(
                                "flex items-center touch-none pointer-events-auto",
                                snapshot.isDragging
                                  ? "cursor-grabbing"
                                  : "cursor-grab"
                              )}
                              style={{
                                color: "hsl(var(--muted-foreground))",
                              }}
                            >
                              <DragIndicatorIcon />
                            </div>

                            <Typography variant="body1" className="flex-1">
                              {canvas.name}
                            </Typography>

                            <div
                              className="flex gap-2 items-center"
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEdit(canvas);
                                }}
                              >
                                <EditIcon />
                              </IconButton>
                              {!canvas.hasCards && (
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(canvas);
                                  }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              )}
                              {canvas.hasCards && (
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleForceDelete(canvas);
                                  }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              )}
                            </div>
                          </Paper>
                        )}
                      </Draggable>
                    ))
                  ) : (
                    <Typography>No canvases found</Typography>
                  )}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>

          <Dialog
            open={editDialogOpen}
            onClose={() => setEditDialogOpen(false)}
            variant="pops"
          >
            <DialogTitle>Edit Canvas</DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                margin="dense"
                label="Canvas Name"
                fullWidth
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="mt-2"
              />
            </DialogContent>
            <DialogActions>
              <Button
                variant="outlined"
                onClick={() => setEditDialogOpen(false)}
                className="normal-case"
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                className="normal-case shadow-none"
              >
                Save
                {isRunning === "save" && (
                  <CircularProgress
                    size={20}
                    style={{ marginLeft: spacingToPx(1) }}
                  />
                )}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={deleteDialogOpen}
            onClose={() => setDeleteDialogOpen(false)}
            variant="pops"
          >
            <DialogTitle>Delete Canvas - {selectedCanvas?.name}</DialogTitle>
            <DialogContent>
              {selectedCanvas?.hasCards && (
                <Typography color="error">
                  This canvas has active cards. Deleting it will delete all
                  cards in the canvas.
                </Typography>
              )}
              <Typography>Are you sure you want to delete it?</Typography>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setDeleteDialogOpen(false)}
                variant="outlined"
                className="normal-case"
              >
                Cancel
              </Button>
              <Button
                color="error"
                variant="contained"
                onClick={confirmDelete}
                className="normal-case shadow-none"
              >
                Delete
                {isRunning === "delete" && (
                  <CircularProgress
                    size={20}
                    style={{ marginLeft: spacingToPx(1) }}
                  />
                )}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={createDialogOpen}
            onClose={() => setCreateDialogOpen(false)}
            variant="pops"
          >
            <DialogTitle>Create New Canvas</DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                margin="dense"
                label="Canvas Name"
                fullWidth
                value={newCanvasName}
                onChange={(e) => setNewCanvasName(e.target.value)}
                className="mt-2"
              />
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setCreateDialogOpen(false)}
                variant="outlined"
                className="normal-case"
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleCreateCanvas}
                disabled={!newCanvasName.trim()}
                className="normal-case"
              >
                Create
                {isRunning === "create" && (
                  <CircularProgress
                    size={20}
                    style={{ marginLeft: spacingToPx(1) }}
                  />
                )}
              </Button>
            </DialogActions>
          </Dialog>
        </div>
      )}
    </>
  );
}
